import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { FeedCurationRunRead } from '../../types/api';
import { createFeedCuration, fetchFeedCuration, listFeedCurations } from './newsApi';

/** How often a pending run is re-read. Same cadence as the planner's chat. */
const POLL_INTERVAL_MS = 2000;

/** Polls before giving up — five minutes at the interval above. */
const POLL_LIMIT = 150;

/** Longest prompt the endpoint accepts. Mirrors the backend's own bound. */
export const FEED_CURATION_MAX_LENGTH = 1000;

export interface FeedCurationResult {
  /** The run being watched, or the last one that finished. Null before a send. */
  run: FeedCurationRunRead | null;
  /** The POST is in flight. Distinct from waiting on the worker. */
  isSubmitting: boolean;
  /** A run is pending and being polled. */
  isWaiting: boolean;
  /** The run never finished inside the poll budget. Polling has stopped. */
  pollTimedOut: boolean;
  /** Why the request could not be made. Not the same as a run that failed. */
  error: string | null;
  /** A run was already outstanding, so this is the one being watched instead. */
  notice: string | null;
  submit: (prompt: string) => Promise<void>;
  /** Pick polling back up after a timeout. */
  retry: () => void;
  /** Forget the finished run so the field is ready for another prompt. */
  reset: () => void;
}

/**
 * One feed-curation run: ask for sources in words, then wait for the worker.
 *
 * The work is done outside the request that starts it, so progress is read by
 * polling the run's own `status` — the server's field, not a local guess. A
 * `setTimeout` chain rather than an interval, so a slow response cannot stack
 * requests, and the whole chain is torn down on unmount or on a new run. A
 * dropped poll says nothing about the run, so the chain spends a poll and
 * carries on; when the budget runs out `pollTimedOut` says so and offers the
 * user a retry rather than a spinner with no end.
 *
 * A 409 `FEED_CURATION_PENDING` is not an error the user can act on: it means
 * their previous ask is still being worked. The hook adopts that run — newest
 * first, `limit: 1` — and waits on it, so the answer still lands on screen.
 *
 * `onFinished` fires once when a run leaves `pending`. A completed run has
 * written `feed_sources` rows, so the caller uses it to re-read its own list.
 *
 * This is the one control on the news surface that reaches a model. It still
 * spends no generation.
 */
export function useFeedCuration(
  businessId: string | null,
  onFinished?: (run: FeedCurationRunRead) => void,
): FeedCurationResult {
  const [run, setRun] = useState<FeedCurationRunRead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [timedOutRunId, setTimedOutRunId] = useState<string | null>(null);

  // Held in a ref so a caller passing a fresh closure each render cannot
  // restart the poll chain mid-wait.
  const finishedRef = useRef(onFinished);
  useEffect(() => {
    finishedRef.current = onFinished;
  }, [onFinished]);

  const runId = run?.id ?? null;
  const isPending = run?.status === 'pending';
  const pollTimedOut = isPending && timedOutRunId === runId;

  useEffect(() => {
    if (!businessId || !runId || !isPending || pollTimedOut) return;

    const controller = new AbortController();
    let cancelled = false;
    let timer: number | undefined;
    let polls = 0;

    function schedule() {
      timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
    }

    async function poll() {
      if (cancelled || !businessId || !runId) return;
      polls += 1;

      try {
        const next = await fetchFeedCuration(businessId, runId, controller.signal);
        if (cancelled) return;
        setRun(next);
        if (next.status !== 'pending') {
          // The run is over: flipping the status tears this effect down. The
          // sources it wrote are on the server, so let the caller re-read them.
          finishedRef.current?.(next);
          return;
        }
      } catch {
        // A dropped poll says nothing about the run. Spend a poll, not the ask.
        if (cancelled || controller.signal.aborted) return;
      }

      if (polls >= POLL_LIMIT) {
        setTimedOutRunId(runId);
        return;
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [businessId, runId, isPending, pollTimedOut]);

  const submit = useCallback(
    async (prompt: string): Promise<void> => {
      if (!businessId) {
        setError('No business context yet.');
        return;
      }

      setIsSubmitting(true);
      setError(null);
      setNotice(null);
      setTimedOutRunId(null);

      try {
        setRun(await createFeedCuration(businessId, prompt));
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === 'FEED_CURATION_PENDING') {
          // Their last ask is still being worked. Watch that one rather than
          // reporting a failure for a request that was never wrong.
          setNotice('Still working on what you asked last.');
          try {
            const [latest] = await listFeedCurations(businessId, 1);
            if (latest) {
              setRun(latest);
            } else {
              setError(getErrorMessage(caught, 'A search is already running.'));
            }
          } catch (lookupFailure) {
            setError(getErrorMessage(lookupFailure, 'A search is already running.'));
          }
        } else {
          setError(getErrorMessage(caught, 'That search could not be started.'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [businessId],
  );

  const retry = useCallback(() => setTimedOutRunId(null), []);

  const reset = useCallback(() => {
    setRun(null);
    setError(null);
    setNotice(null);
    setTimedOutRunId(null);
  }, []);

  return {
    run,
    isSubmitting,
    isWaiting: isPending && !pollTimedOut,
    pollTimedOut,
    error,
    notice,
    submit,
    retry,
    reset,
  };
}
